import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// Cambio manual de nivel (solo administrador principal), con motivo y opción de fijarlo hasta fin de año.
export function ChangeTierDialog({ user, onClose, onDone }: { user: any | null; onClose: () => void; onDone: () => void }) {
  const [tiers, setTiers] = useState<any[]>([]);
  const [tier, setTier] = useState("");
  const [reason, setReason] = useState("");
  const [lock, setLock] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setTier(user.tier); setReason(""); setError(""); setLock(true);
    api.get("/tiers").then((r) => setTiers([...r.data].sort((a, b) => a.min_points - b.min_points))).catch(() => setTiers([]));
  }, [user]);

  const idx = (name: string) => tiers.findIndex((t) => t.name === name);
  const lowering = user && idx(tier) >= 0 && idx(tier) < idx(user.tier);
  // Bajar sin fijar no dura: con sus puntos del año volvería a subir en el próximo movimiento.
  useEffect(() => { if (lowering) setLock(true); }, [lowering]);

  const submit = async () => {
    if (tier === user.tier && lock === !!user.tier_locked) { setError("Elige un nivel distinto al actual."); return; }
    if (reason.trim().length < 3) { setError("Escribe el motivo del cambio."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/tier`, { tier, reason: reason.trim(), lock });
      toast.success(`Nivel de ${user.name}: ${tier}`);
      onClose(); onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o: boolean) => !o && !busy && onClose()}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900 sm:max-w-md" data-testid="change-tier-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Cambiar nivel</DialogTitle>
          <DialogDescription className="text-slate-600">{user?.name} · hoy {user?.tier}{user?.tier_locked ? " (fijado)" : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <fieldset>
            <legend className="text-sm text-slate-700 mb-2">Nuevo nivel</legend>
            <div className="flex flex-wrap gap-2">
              {tiers.map((t) => (
                <label key={t.name} className={`px-3 py-1.5 rounded-full border text-sm cursor-pointer ${tier === t.name ? "border-navy bg-navy/5 text-navy font-medium" : "border-slate-200 text-slate-700"}`}>
                  <input type="radio" name="tier" value={t.name} checked={tier === t.name} onChange={() => { setTier(t.name); setError(""); }} className="sr-only" />
                  {t.name}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={lock} disabled={lowering} onChange={(e) => setLock(e.target.checked)} className="mt-0.5 accent-navy" data-testid="change-tier-lock" />
            <span>Fijar este nivel hasta el 31 de diciembre
              <span className="block text-xs text-slate-600">{lowering ? "Al bajar de nivel queda fijo: si no, volvería a subir con sus puntos del año." : "Fijado, no sube solo aunque gane puntos. Sin fijar, sigue subiendo si califica."}</span>
            </span>
          </label>
          <div>
            <Label htmlFor="tier-reason" className="text-sm text-slate-700">Motivo * (queda en el historial)</Label>
            <Textarea id="tier-reason" rows={2} maxLength={300} value={reason} onChange={(e: any) => { setReason(e.target.value); setError(""); }} className="mt-1.5" data-testid="change-tier-reason" />
          </div>
          {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
          <p className="text-xs text-slate-600">El cliente recibe un aviso con su nivel nuevo. Su saldo de puntos no cambia.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="change-tier-confirm" className="gold-gradient-bg text-white font-semibold rounded-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" /> : "Cambiar nivel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
